'use client';

import { AnimatePresence, motion } from 'motion/react';
import Form from 'next/form';
import Image from 'next/image';
import Link from 'next/link';
import { useCallback, useState } from 'react';
import { useFormStatus } from 'react-dom';
import { z } from 'zod';

import CallToAction from '~/components/atoms/CallToAction';
import Heading from '~/components/atoms/Heading';
import Wrap from '~/components/atoms/Wrap';
import LayoutGrid from '~/components/molecules/LayoutGrid';
import { fetchNextPage } from '~/lib/actions/paginated-blog-index';
import { modelPaginatedPosts } from '~/lib/api/models';
import routeGenerator from '~/lib/api/routes';
import { getMainPostCategory } from '~/lib/utils/posts';

interface PaginationFormContentProps {
  hasNextPage?: boolean;
}

const PaginationFormContent: React.FC<PaginationFormContentProps> = ({
  hasNextPage,
}) => {
  const { pending } = useFormStatus();

  return hasNextPage ? (
    <CallToAction
      as="button"
      type="submit"
      variant="dimmed"
      loading={pending}
      disabled={pending}
    >
      Afficher plus de résultats
    </CallToAction>
  ) : (
    <p className="text-brand-quinary text-xs">Vous avez vu tous les articles</p>
  );
};

interface Props {
  initialPosts: z.infer<typeof modelPaginatedPosts.schema>;
  postsPerPage: number;
}

const PaginatedBlogIndex: React.FC<Props> = ({
  initialPosts,
  postsPerPage,
}) => {
  const [pages, setPages] = useState<
    z.infer<typeof modelPaginatedPosts.schema>[]
  >([initialPosts]);

  const lastPaginationData = pages.at(-1)?.posts.pageInfo;
  const hasNextPage = !!lastPaginationData?.hasNextPage;

  const loadNextPage = useCallback(async () => {
    const nextPage = await fetchNextPage({
      perPage: postsPerPage,
      after: lastPaginationData?.endCursor,
    });

    if (nextPage) {
      setPages((previousPages) => [...previousPages, nextPage.data]);
    }
  }, [lastPaginationData?.endCursor, postsPerPage]);

  return (
    <LayoutGrid classNames={{ inner: 'gap-10 pb-2' }}>
      <motion.ul className="col-span-2 col-start-1 grid grid-cols-3 gap-10 max-lg:grid-cols-1 max-lg:gap-2 lg:col-span-12">
        <AnimatePresence initial={false}>
          {pages.map((page) =>
            page.posts.edges.map((post, postIndex) => {
              const mainCategory = getMainPostCategory(post.node);
              const y = Math.floor(postIndex / 3);
              const x = postIndex % 3;
              const diagonalIndex = x + y;

              if (mainCategory) {
                return (
                  <motion.li
                    key={post.node.id}
                    className="bg-brand-quaternary relative isolate overflow-clip"
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity: 1,
                      transition: {
                        delay: 0.1 * diagonalIndex,
                        duration: 0.6,
                        ease: 'easeOut',
                      },
                    }}
                    layout
                  >
                    <article className="group/article flex aspect-440/320 w-full">
                      <Link
                        href={routeGenerator.post({
                          slug: post.node.slug,
                          category: mainCategory.slug,
                        })}
                        className="flex h-full w-full"
                      >
                        {post.node.featuredImage && (
                          <Image
                            src={post.node.featuredImage.node.sourceUrl}
                            alt={post.node.featuredImage.node.altText}
                            sizes={post.node.featuredImage.node.sizes}
                            placeholder={
                              post.node.featuredImage.node.blurDataUrl
                                ? 'blur'
                                : 'empty'
                            }
                            blurDataURL={
                              post.node.featuredImage.node.blurDataUrl ||
                              undefined
                            }
                            className="absolute top-0 left-0 -z-10 h-full w-full object-cover transition-transform duration-700 group-hover/article:scale-105"
                            fill
                            unoptimized
                          />
                        )}

                        <div className="flex h-full w-full flex-col justify-end gap-y-2 bg-linear-to-b to-black/80 p-4 text-white lg:p-6">
                          <p className="text-sm">{mainCategory.name}</p>

                          <Heading as="h2" level={4} className="font-sans">
                            {post.node.title}
                          </Heading>
                        </div>
                      </Link>
                    </article>
                  </motion.li>
                );
              }
            })
          )}
        </AnimatePresence>
      </motion.ul>

      <div className="col-span-2 col-start-1 mb-10 flex items-center justify-center lg:col-span-12">
        <Wrap
          with={(children) => <Form action={loadNextPage}>{children}</Form>}
          when={hasNextPage}
        >
          <PaginationFormContent hasNextPage={hasNextPage} />
        </Wrap>
      </div>
    </LayoutGrid>
  );
};

export default PaginatedBlogIndex;
